/**
 * SectionCard — one card per rendered proposal section on the
 * `/proposals/{id}/draft` view (Phase G4B CP2).
 *
 * Two visual states:
 *   - **passed**: markdown-lite paragraphs + confidence tier chip +
 *     "Regenerate this section" + "Edit inline" (textarea toggle,
 *     PATCH on save).
 *   - **fail_closed**: FAIL_CLOSED_FALLBACK message + fail_reason
 *     chip (factual label from FAIL_REASON_LABELS) + prominent
 *     "Draft manually" button (opens inline edit) + "Regenerate"
 *     button.
 *
 * Regenerate flow:
 *   1. Click "Regenerate this section" → confirmation state
 *      ("Regenerate {Title}? This uses 1 section-generation slot.").
 *   2. Confirm → POST /api/proposals/{id}/sections/{key}/regenerate.
 *   3. Card-local spinner during in-flight.
 *   4. Response:
 *        200 ProposalPublic → replace section in-place
 *        200 llm_disabled → inline banner within the card
 *        402 generation_budget_exhausted → inline banner
 *        409 generation_in_progress → inline banner
 *
 * Inline edit flow:
 *   1. Click "Edit inline" (or "Draft manually" on fail-closed).
 *   2. Textarea appears with current content.
 *   3. On blur (save), PATCH /api/proposals/{id}/sections/{key}.
 *   4. Server stamps status=passed, confidence=1.0, cap NOT consumed.
 *
 * Voice discipline: all copy is factual + neutral. Confidence
 * adjectives verbatim from `confidenceTier`: "Moderate" / "High" /
 * "Very high". Fail-reason labels verbatim from FAIL_REASON_LABELS.
 */
import { useEffect, useRef, useState } from "react";
import {
  Pencil,
  RefreshCw,
  Loader2,
  ShieldAlert,
  Check,
  X,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import {
  SECTION_DISPLAY_TITLES,
  confidenceTier,
  failReasonLabel,
} from "@/components/proposals/utils";


function formatResetDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}


export default function SectionCard({
  proposalId,
  sectionKey,
  section,   // {content, status, fail_reason, confidence, generated_at, prompt_version}
  onUpdated, // (updatedProposalPublic) => void — replaces the parent's proposal state
  onBudgetChanged, // () => void — parent refetches the budget indicator
}) {
  const title = SECTION_DISPLAY_TITLES[sectionKey] || sectionKey;
  const isFailClosed = section?.status === "fail_closed";
  const content = section?.content || "";
  const confidence = section?.confidence;
  const tier = confidenceTier(confidence);

  const [confirmingRegen, setConfirmingRegen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const textareaRef = useRef(null);

  // Sync local draft when the underlying section is replaced (e.g.
  // after a successful regeneration).
  useEffect(() => {
    if (!editing) setDraft(content);
  }, [content, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  async function doRegenerate() {
    setBanner(null);
    setRegenerating(true);
    setConfirmingRegen(false);
    try {
      const res = await api.post(
        `/proposals/${proposalId}/sections/${sectionKey}/regenerate`,
        {},
        { timeout: 30_000 },
      );
      if (res.data && res.data.code === "llm_disabled") {
        setBanner({
          kind: "warning",
          title: "AI section generation is temporarily unavailable",
          body: res.data.message,
        });
        return;
      }
      onUpdated(res.data);
      onBudgetChanged?.();
      toast.success(`Regenerated "${title}"`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || {};
      if (status === 402 && detail.code === "generation_budget_exhausted") {
        const reset = formatResetDate(detail.next_reset_at);
        setBanner({
          kind: "warning",
          title: "Generation budget is fully used",
          body:
            `You have used ${detail.cap - detail.remaining} of your ${detail.cap} ` +
            `section-generations in the last ${detail.window_days} days.` +
            (reset ? ` Next slot opens on ${reset}.` : ""),
        });
        onBudgetChanged?.();
        return;
      }
      if (status === 409 && detail.code === "generation_in_progress") {
        setBanner({
          kind: "warning",
          title: "Another generation is already running for this proposal",
          body:
            "Please wait for it to complete. The soft-lock releases automatically after five minutes.",
        });
        return;
      }
      // Premium vs module-disabled require different UX. Fall back
      // to a generic error banner (regenerate is a fine-grained
      // action; global-scope panels don't fit here — the parent
      // /draft view already handles page-level premium/feature_disabled
      // gating). G4B CP2 rerun P0 fix.
      if (detail.code === "proposals_premium_only") {
        setBanner({
          kind: "warning",
          title: "Premium required",
          body: "Section regeneration is available on Premium.",
        });
        return;
      }
      if (detail.code === "feature_disabled") {
        setBanner({
          kind: "warning",
          title: "Grant Writer is temporarily disabled",
          body:
            "An administrator has paused the Grant Writer module. Your section content is preserved.",
        });
        return;
      }
      setBanner({
        kind: "error",
        title: "Regeneration failed",
        body: formatApiError(err),
      });
    } finally {
      setRegenerating(false);
    }
  }

  async function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === content) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(
        `/proposals/${proposalId}/sections/${sectionKey}`,
        { content: trimmed },
      );
      onUpdated(res.data);
      toast.success(`Saved "${title}"`);
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setDraft(content);
    setEditing(false);
  }

  const paragraphs = (content || "").split(/\n\n+/).filter((p) => p.trim().length > 0);

  return (
    <article
      data-testid={`section-card-${sectionKey}`}
      className={`rounded-3xl border p-6 md:p-7 flex flex-col gap-4 ${
        isFailClosed
          ? "border-amber-500/40 bg-amber-500/5"
          : "border-v2-border bg-v2-surface"
      }`}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3
            data-testid={`section-card-title-${sectionKey}`}
            className="text-v2-heading-sm font-semibold text-v2-text"
          >
            {title}
          </h3>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isFailClosed ? (
              <span
                data-testid={`section-fail-reason-${sectionKey}`}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-v2-caption uppercase tracking-[0.14em]"
                style={{
                  background: "rgba(251,191,36,0.14)",
                  border: "1px solid rgba(251,191,36,0.42)",
                  color: "rgb(251,191,36)",
                }}
              >
                <AlertCircle className="w-3 h-3" />
                {failReasonLabel(section?.fail_reason)}
              </span>
            ) : (
              <span
                data-testid={`section-confidence-${sectionKey}`}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-v2-caption uppercase tracking-[0.14em]"
                style={{
                  background: tier.tintBg,
                  border: `1px solid ${tier.tintBorder}`,
                  color: tier.tint,
                }}
              >
                {tier.label}
                {confidence != null && (
                  <span className="tabular-nums">
                    · {(confidence * 100).toFixed(0)}%
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!editing && !confirmingRegen && (
            <>
              <button
                type="button"
                data-testid={`section-edit-${sectionKey}`}
                onClick={() => {
                  setDraft(isFailClosed ? "" : content);
                  setEditing(true);
                }}
                disabled={regenerating}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm border transition-colors disabled:opacity-60 ${
                  isFailClosed
                    ? "bg-amber-500/20 text-amber-100 border-amber-500/40 hover:bg-amber-500/30"
                    : "bg-v2-surfaceSubtle text-v2-text border-v2-border hover:bg-v2-surface"
                }`}
              >
                <Pencil className="w-3.5 h-3.5" />
                {isFailClosed ? "Draft manually" : "Edit inline"}
              </button>
              <button
                type="button"
                data-testid={`section-regenerate-${sectionKey}`}
                onClick={() => setConfirmingRegen(true)}
                disabled={regenerating}
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm text-v2-textMuted hover:text-v2-text bg-transparent hover:bg-v2-surfaceSubtle border border-v2-border transition-colors disabled:opacity-60"
              >
                {regenerating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Regenerate
              </button>
            </>
          )}
        </div>
      </header>

      {confirmingRegen && (
        <div
          data-testid={`section-regenerate-confirm-${sectionKey}`}
          className="rounded-2xl border border-v2-accent/40 bg-v2-accentSoft p-4 flex items-start gap-3"
        >
          <ShieldAlert className="w-4 h-4 flex-shrink-0 text-v2-accent mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-v2-body-sm text-v2-text">
              Regenerate <strong>{title}</strong>? This uses 1
              section-generation slot from your rolling 30-day budget.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                data-testid={`section-regenerate-confirm-btn-${sectionKey}`}
                onClick={doRegenerate}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-v2-caption font-semibold text-v2-accentInk bg-v2-accent hover:bg-v2-accentHover transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Yes, regenerate
              </button>
              <button
                type="button"
                data-testid={`section-regenerate-cancel-${sectionKey}`}
                onClick={() => setConfirmingRegen(false)}
                className="text-v2-caption text-v2-textMuted hover:text-v2-text"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {banner && (
        <div
          data-testid={`section-banner-${banner.kind}-${sectionKey}`}
          className={`rounded-2xl border p-3 flex items-start gap-2 ${
            banner.kind === "error"
              ? "border-red-500/40 bg-red-500/10 text-red-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-v2-caption font-semibold">{banner.title}</p>
            <p className="text-v2-caption mt-1 opacity-90">{banner.body}</p>
          </div>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss banner"
            className="text-v2-textMuted hover:text-v2-text p-1"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Body */}
      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            ref={textareaRef}
            data-testid={`section-edit-textarea-${sectionKey}`}
            rows={10}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveEdit}
            className="w-full rounded-2xl border border-v2-border bg-v2-background p-4 text-v2-body-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent/40 resize-y"
            placeholder="Draft this section in your own words. Save happens automatically when you click outside the box."
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid={`section-edit-save-${sectionKey}`}
              onClick={saveEdit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm font-semibold text-v2-accentInk bg-v2-accent hover:bg-v2-accentHover transition-colors disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
              Save
            </button>
            <button
              type="button"
              data-testid={`section-edit-cancel-${sectionKey}`}
              onClick={cancelEdit}
              className="text-v2-caption text-v2-textMuted hover:text-v2-text"
            >
              Cancel
            </button>
            <span className="text-v2-caption text-v2-textMuted">
              Save also fires automatically when you click outside the box.
            </span>
          </div>
        </div>
      ) : isFailClosed ? (
        <div className="rounded-2xl bg-amber-500/5 border border-amber-500/20 p-4">
          <p className="text-v2-body-sm text-amber-200">
            {content}
          </p>
        </div>
      ) : (
        <div
          data-testid={`section-content-${sectionKey}`}
          className="prose prose-invert max-w-none space-y-3"
        >
          {paragraphs.length === 0 ? (
            <p className="text-v2-body-sm text-v2-textMuted italic">
              (Empty section)
            </p>
          ) : (
            paragraphs.map((para, i) => (
              <p
                key={i}
                className="text-v2-body-sm text-v2-text leading-relaxed"
              >
                {para}
              </p>
            ))
          )}
        </div>
      )}
    </article>
  );
}
